import { Component, Input, OnInit } from '@angular/core';
import { BaseProfileComponent } from '../base-profile.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  UserProfileAgentModel,
  UserProfileLookups,
} from 'src/app/core/models/user-profile/user-profile.model';
import { TranslateService } from '@ngx-translate/core';
import { ProfileMapper } from '../profile-mapper';
import { InputPatterns } from 'src/app/shared/input-components/validators/InputPatterns';
import { RegistrationService } from 'src/app/core/api-services/user-profile/registration.service';
import { AlertImportanceEnum } from 'src/app/shared/bs-alert/AlertImportanceEnum';
import { AlertOptions } from 'src/app/shared/bs-alert/AlertOptions';
import { EsOtpDto } from 'src/app/core/api-services/shared/EsOtpDto';
import { UserProfileService } from 'src/app/core/api-services/user-profile/user-profile.service';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { catchError, switchMap } from 'rxjs/operators';

@Component({
  selector: 'profile-agents',
  templateUrl: 'profile-agents.component.html',
})
export class ProfileAgentsComponent
  extends BaseProfileComponent
  implements OnInit
{
  @Input() mainForm: FormGroup;
  @Input() agents: UserProfileAgentModel[];
  @Input() lookups: UserProfileLookups;
  @Input() isOwner: boolean = false;
  @Input() isRegistered: boolean = false;

  get activeAgents() {
    return this.agents?.filter(
      (a) => a.IsDeleted == null || a.IsDeleted == false
    );
  }

  form: FormGroup;
  showForm: boolean = false;
  isNew: boolean = false;
  isEditable: boolean = false;
  showControls: boolean = false;
  otpId: string;
  otpAlert: AlertOptions;
  otpAlertType: AlertImportanceEnum;
  searchText: string;
  currentPage: number = 1;
  itemsPerPage: number = 10;
  public isCollapsed: boolean[] = [];
  selectedAgent: UserProfileAgentModel;

  constructor(
    translateService: TranslateService,
    protected _mapper: ProfileMapper,
    private _registrationService: RegistrationService,
    private _userProfileService: UserProfileService
  ) {
    super(translateService, _mapper);
  }

  ngOnInit(): void {
    this.mainForm.addControl(
      'Agents',
      new FormControl(null)
    );
    this.initializeForm();
  }

  updateSearchText($event: any) {
    this.currentPage = 1;
    this.searchText = $event.searchText;
  }

  addAgentClick() {
    this.isNew = this.showForm = true;
    this.initializeForm();
  }

  edit($event: any, agent: UserProfileAgentModel) {
    this.isNew = false;
    this.showForm = true;
    this.selectedAgent = agent;
    this.initializeForm();
    if (agent.IdentityType) {
      this.idTypeChange(agent.IdentityType);
      if (agent.IdentityType == 'IdNumber') {
        this.showControls = true;
        this.isEditable = false;
      }
    }
    this.setFormValue(agent);
  }

  delete(agent: UserProfileAgentModel) {
    const sweet = Swal.fire({
      title: this.translateService.instant(
        'UserProfile.Agent.AreYouSureYouWantToDeleteTheAgent'
      ),
      icon: this.translateService.instant(
        'UserProfile.Agent.ConfirmBoxWarning'
      ),
      showCancelButton: true,
      confirmButtonText: this.translateService.instant(
        'UserProfile.Agent.YesDelete'
      ),
      cancelButtonText: this.translateService.instant(
        'UserProfile.Agent.NoKeep'
      ),
    });

    sweet.then((result: SweetAlertResult) => {
      if (result.isConfirmed) {
        if (agent.Id) {
          agent.IsDeleted = true;
        } else {
          this.agents.splice(this.agents.indexOf(agent), 1);
        }
        if (this.isRegistered) {
          this._userProfileService
            .DeleteAgent(agent.Id)
            .subscribe((response) => {
              Swal.fire(
                this.translateService.instant(
                  'UserProfile.Agent.AgentDeletedSuccessfully'
                ),
                '',
                this.translateService.instant(
                  'UserProfile.Agent.ConfirmBoxSuccess'
                )
              );
            });
        }
      }
    });
  }

  onIdTypeChange($event: any) {
    this.resetForm(['IdentityType']);
    if ($event.new) {
      this.idTypeChange($event.new);
    }
  }

  async onIDChange() {
    this.resetForm(['IdentityType', 'IdNumber']);
    var idNumber = this.form.controls.IdNumber.value;

    if (idNumber && idNumber.length >= 18) {
      this._registrationService
        .ValidateIdNumber(idNumber)
        .subscribe((response) => {
          if (response?.Data?.IsValid) {
            this.otpId = response.Data.RegistrationID;
            this.otpAlertType = AlertImportanceEnum.Info;
            this.otpAlert = {
              display: true,
              text: this.translateService.instant('trApplicantOtpSentMessage', {
                otpMobileNo: response.Data.MobileNumber,
              }),
            };
            this.form.addControl('OTP', new FormControl());
          } else {
            this.otpId = null;
            this.otpAlertType = AlertImportanceEnum.Danger;
            this.otpAlert = {
              display: true,
              text: this.translateService.instant(
                `trApplicantEmiratesIdErrors.${response.Data.Status}`
              ),
            };
            this.form.removeControl('OTP');
          }
        });
    }
  }

  async onOTPChange() {
    var otp = this.form.controls.OTP.value;
    if (otp && otp.length >= 5) {
      var otpModel: EsOtpDto = {
        RegistrationID: this.otpId,
        OTP: otp,
      };
      this._registrationService
        .ValidateOTP(otpModel)
        .pipe(
          switchMap((response) => {
            if (response?.Data) {
              this.form.controls.NameAr.setValue(response.Data.NameAr);
              this.form.controls.NameEn.setValue(response.Data.Name);
              this.form.controls.Mobile.setValue(response.Data.Phone);
              return this._userProfileService.AgentExist(
                this.form.controls.IdNumber.value
              );
            } else {
              this.otpAlertType = AlertImportanceEnum.Danger;
              this.otpAlert = {
                display: true,
                text: this.translateService.instant(
                  `UserProfile.Agent.OTPNotCorrect`
                ),
              };
              return null;
            }
          }),
          catchError((error) => {
            this.otpAlertType = AlertImportanceEnum.Danger;
            this.otpAlert = {
              display: true,
              text: this.translateService.instant(
                `UserProfile.Agent.OTPNotCorrect`
              ),
            };
            return null;
          })
        )
        .subscribe((agentExist) => {
          if (agentExist) {
            this.otpId = null;
            this.otpAlertType = AlertImportanceEnum.Danger;
            this.otpAlert = {
              display: true,
              text: this.translateService.instant(
                `UserProfile.Agent.AgentExistError`
              ),
            };
            this.form.removeControl('OTP');
            return null;
          } else {
            this.otpAlert = null;
            this.form.removeControl('OTP');
            this.showControls = true;
            this.isEditable = false;
          }
        });
    }
  }

  onPassportChange() {
    this._userProfileService
      .AgentExist(this.form.controls.Passport.value)
      .subscribe((agentExist) => {
        if (agentExist) {
          this.otpId = null;
          this.otpAlertType = AlertImportanceEnum.Danger;
          this.otpAlert = {
            display: true,
            text: this.translateService.instant(
              `UserProfile.Agent.AgentExistError`
            ),
          };
          this.form.removeControl('OTP');
          return null;
        } else {
          this.otpAlert = null;
        }
      });
  }

  cancel() {
    this.showForm = this.isNew = false;
  }

  save() {
    if (this.form.valid) {
      let agent = this.form.getRawValue();
      if (this.isNew) {
        this.agents.push(agent);
      } else {
        Object.assign(this.selectedAgent, this.updateAgent(agent));
        if (this.selectedAgent.Id) {
          this.selectedAgent.IsUpdated = true;
        }
      }
      this._userProfileService
        .AddAgent(this._mapper.mapAgentDataToAgentDto(agent))
        .subscribe((response) => {
          agent.Id = this.selectedAgent.Id = response;

          Swal.fire(
            this.translateService.instant(
              this.isNew
                ? 'UserProfile.Agent.AgentAddedSuccessfully'
                : 'UserProfile.Agent.AgentUpdatedSuccessfully'
            ),
            '',
            this.translateService.instant('UserProfile.Agent.ConfirmBoxSuccess')
          );
        });
      this.resetForm();
      this.showForm = this.showControls = this.isEditable = false;
    } else {
      this.form.markAllAsTouched();
    }
  }

  changeLoginPermission($event: any, agent: UserProfileAgentModel) {
    this._userProfileService
      .EnableAgentLogin(agent.Id)
      .subscribe((response) => {
        Swal.fire(
          this.translateService.instant(
            'UserProfile.Agent.LoginPermissionUpdatedSuccessfully'
          ),
          '',
          this.translateService.instant('UserProfile.Agent.ConfirmBoxSuccess')
        );
      });
  }

  private initializeForm() {
    this.form = new FormGroup({
      IdentityType: new FormControl(null, Validators.required),
      NameAr: new FormControl(null, [
        Validators.required,
        Validators.pattern(InputPatterns.NonNumbericEnglishOrArabic),
      ]),
      NameEn: new FormControl(null, [
        Validators.required,
        Validators.pattern(InputPatterns.NonNumbericEnglish),
      ]),
      Mobile: new FormControl(null, Validators.required),
      Email: new FormControl(null, [
        Validators.required,
        Validators.pattern(InputPatterns.Email),
      ]),
      LoginPermission: new FormControl(null),
      PreferedLanguage: new FormControl(null, Validators.required),
    });
    this.isEditable = this.showControls = false;
  }

  private idTypeChange(idType: string) {
    if (idType == 'IdNumber') {
      this.form.removeControl('Passport');
      this.form.addControl(
        'IdNumber',
        new FormControl(null, Validators.required)
      );
      this.showControls = this.isEditable = false;
    } else if (idType == 'Passport') {
      this.form.removeControl('IdNumber');
      this.form.removeControl('OTP');
      this.form.addControl(
        'Passport',
        new FormControl(null, Validators.required)
      );
      this.showControls = this.isEditable = true;
    }
  }

  private updateAgent(agent: UserProfileAgentModel): UserProfileAgentModel {
    agent.PreferedLanguageCode = agent.PreferedLanguage.code;
    return agent;
  }

  checkAgentExist(IdentityNumber: string) {
    this._userProfileService
      .AgentExist(IdentityNumber)
      .subscribe((response) => {
        return response;
      });
  }
}
